import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toTitleCase } from "@/lib/text-utils";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Download,
  Loader2,
  Play,
  FileText,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
  MoreVertical,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import QueryEditor from "./QueryEditor";
import PlaceholderInput from "./PlaceholderInput";
import { toast } from "sonner";
import { format } from "date-fns";
import { DashboardFilterBar, type ActiveFilterValues } from "@/components/DashboardFilterBar";
import type { ConfiguredFilter } from "@/components/FilterSuggestionsDialog";
import { usePiiMasking } from "@/hooks/usePiiMasking";
import { useDownloadLimit } from "@/hooks/useDownloadLimit";
import MaskedValue from "@/components/MaskedValue";
import {
  extractPlaceholders,
  inferPlaceholderInfo,
  replacePlaceholders,
  type PlaceholderInfo,
} from "@/lib/query-placeholders";
import { useMcpSchema } from "@/hooks/useMcpSchema";

interface ReportFilter {
  column: string;
  operator: string;
  value: string;
  label: string;
}

interface ReportQuery {
  id: string;
  name: string;
  description: string | null;
  sql_query: string;
  database_connection_id: string | null;
  filter_config: any;
}

function parseFilterConfig(raw: any): { reportFilters: ReportFilter[]; configuredFilters: ConfiguredFilter[] } {
  if (Array.isArray(raw)) return { reportFilters: raw, configuredFilters: [] };
  if (raw && typeof raw === "object") {
    return {
      reportFilters: Array.isArray(raw.reportFilters) ? raw.reportFilters : [],
      configuredFilters: Array.isArray(raw.configuredFilters) ? raw.configuredFilters : [],
    };
  }
  return { reportFilters: [], configuredFilters: [] };
}

interface Props {
  reportId: string;
  onBack?: () => void;
}

type SortDirection = "asc" | "desc" | null;

const ReportDownloadView = ({ reportId, onBack }: Props) => {
  const { profile } = useAuth();
  const { shouldMask, isPiiColumn, isUnmasked, requestUnmask } = usePiiMasking();
  const { remaining: downloadRemaining, dailyLimit, todayUsed, checkAndLog } = useDownloadLimit();
  const { schemaData } = useMcpSchema();
  const [report, setReport] = useState<ReportQuery | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<Date | null>(null);
  const [results, setResults] = useState<any[] | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [activeFilterValues, setActiveFilterValues] = useState<ActiveFilterValues>({});

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Column filter state
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  // Placeholder state
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});

  // Query editor dialog state
  const [editQueryData, setEditQueryData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("saved_queries" as any)
        .select("id, name, description, sql_query, database_connection_id, filter_config")
        .eq("id", reportId)
        .single();

      if (error || !data) {
        toast.error("Report not found");
        onBack?.();
        return;
      }

      const r = data as any as ReportQuery;
      setReport(r);

      // Initialize filter values with defaults
      const defaults: Record<string, string> = {};
      const parsed = parseFilterConfig(r.filter_config);
      parsed.reportFilters.forEach((f) => {
        defaults[f.column] = f.value || "";
      });
      setFilterValues(defaults);
      setLoading(false);
    };

    fetchReport();
  }, [reportId]);

  const parsedFilters = report ? parseFilterConfig(report.filter_config) : { reportFilters: [], configuredFilters: [] };

  // Detect placeholders in the query SQL
  const baseSql = report?.sql_query || "";
  const placeholderNames = useMemo(() => extractPlaceholders(baseSql), [baseSql]);
  const placeholderInfos: PlaceholderInfo[] = useMemo(
    () => placeholderNames.map((name) => inferPlaceholderInfo(baseSql, name, schemaData)),
    [placeholderNames, baseSql, schemaData],
  );

  // Reset placeholder values when placeholder set changes, applying defaults
  useEffect(() => {
    setPlaceholderValues((prev) => {
      const next: Record<string, string> = {};
      for (const ph of placeholderInfos) {
        next[ph.name] = prev[ph.name] ?? ph.defaultValue ?? "";
      }
      return next;
    });
  }, [placeholderNames.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildFilteredSql = () => {
    if (!report?.sql_query) return null;
    let sql = report.sql_query.replace(/;\s*$/, "");

    // Replace {{placeholder}} tokens with user-supplied values
    if (placeholderNames.length > 0) {
      sql = replacePlaceholders(sql, placeholderValues, placeholderInfos);
    }

    const conditions: string[] = [];

    // Manual report filters (WHERE conditions)
    parsedFilters.reportFilters.forEach((f) => {
      const val = filterValues[f.column];
      if (!f.column || val === undefined || val === "") return;
      if (f.operator === "IS NULL" || f.operator === "IS NOT NULL") {
        conditions.push(`${f.column} ${f.operator}`);
      } else if (f.operator === "IN") {
        const items = val
          .split(",")
          .map((v) => `'${v.trim().replace(/'/g, "''")}'`)
          .join(", ");
        conditions.push(`${f.column} IN (${items})`);
      } else if (f.operator === "LIKE") {
        conditions.push(`${f.column} LIKE '${val.replace(/'/g, "''")}'`);
      } else {
        conditions.push(`${f.column} ${f.operator} '${val.replace(/'/g, "''")}'`);
      }
    });

    // Configured (schema-aware) filters from DashboardFilterBar
    parsedFilters.configuredFilters.forEach((cf) => {
      const val = activeFilterValues[cf.id];
      if (val === undefined || val === null || val === "") return;
      if (Array.isArray(val)) {
        if (val.length === 0) return;
        const items = val.map((v) => `'${String(v).replace(/'/g, "''")}'`).join(", ");
        conditions.push(`${cf.fieldName} IN (${items})`);
      } else if (val instanceof Date) {
        conditions.push(`${cf.fieldName} = '${format(val, "yyyy-MM-dd")}'`);
      } else {
        const strVal = String(val);
        if (!strVal) return;
        if (cf.filterType === "number") {
          conditions.push(`${cf.fieldName} = ${strVal}`);
        } else if (cf.filterType === "text") {
          conditions.push(`${cf.fieldName} LIKE '%${strVal.replace(/'/g, "''")}%'`);
        } else {
          conditions.push(`${cf.fieldName} = '${strVal.replace(/'/g, "''")}'`);
        }
      }
    });

    if (conditions.length === 0) return sql;

    // Check if SQL already has WHERE
    const hasWhere = /\bWHERE\b/i.test(sql);
    const conditionStr = conditions.join(" AND ");
    if (hasWhere) {
      sql = sql.replace(/\bWHERE\b/i, `WHERE ${conditionStr} AND`);
    } else {
      // Insert before GROUP BY, ORDER BY, LIMIT, or at end
      const insertPoint = sql.search(/\b(GROUP\s+BY|ORDER\s+BY|LIMIT)\b/i);
      if (insertPoint > -1) {
        sql = sql.slice(0, insertPoint) + ` WHERE ${conditionStr} ` + sql.slice(insertPoint);
      } else {
        sql += ` WHERE ${conditionStr}`;
      }
    }

    return sql;
  };

  const handleRun = async () => {
    // Validate that all placeholders have values
    if (placeholderNames.length > 0) {
      const missing = placeholderNames.filter((n) => !placeholderValues[n]?.trim());
      if (missing.length > 0) {
        toast.error(`Please fill in: ${missing.join(", ")}`);
        return;
      }
    }

    const sql = buildFilteredSql();
    if (!sql) return toast.error("No query found");

    setRunning(true);
    setResults(null);
    setSortColumn(null);
    setSortDirection(null);
    setColumnFilters({});

    try {
      // Use the same backend as query page Test — mcp-api handles DB routing via session
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
        method: "POST",
        headers,
        body: JSON.stringify([sql]),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }
      const data = await res.json();
      let rows: any[] = [];
      if (Array.isArray(data)) {
        const first = data[0];
        rows = Array.isArray(first) ? first : first?.rows || first?.result || data;
      } else if (data?.results) {
        const first = Array.isArray(data.results) ? data.results[0] : data.results;
        rows = Array.isArray(first) ? first : first?.rows || first?.result || [];
      } else if (data?.rows) {
        rows = data.rows;
      }
      setResults(rows);
      setColumns(rows.length > 0 ? Object.keys(rows[0]) : []);
      setLastRunAt(new Date());
    } catch (err: any) {
      toast.error(err.message || "Query failed");
    } finally {
      setRunning(false);
    }
  };

  // Filtered + sorted results
  const processedResults = useMemo(() => {
    if (!results) return null;
    let rows = results;

    // Apply column filters
    const activeColFilters = Object.entries(columnFilters).filter(([, v]) => v.trim() !== "");
    if (activeColFilters.length > 0) {
      rows = rows.filter((row) =>
        activeColFilters.every(([col, filterVal]) => {
          const cellVal = row[col];
          if (cellVal === null || cellVal === undefined) return false;
          return String(cellVal).toLowerCase().includes(filterVal.toLowerCase());
        }),
      );
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      rows = [...rows].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
        }
        const aStr = String(aVal);
        const bStr = String(bVal);
        return sortDirection === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    return rows;
  }, [results, columnFilters, sortColumn, sortDirection]);

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(col);
      setSortDirection("asc");
    }
  };

  const hasActiveColumnFilters = Object.values(columnFilters).some((v) => v.trim() !== "");

  const downloadCsv = async () => {
    if (!results || results.length === 0) return;

    // Check daily download limit
    const { allowed, remaining } = await checkAndLog(results.length);
    if (!allowed) {
      toast.error(
        `Daily download limit reached. You have used ${dailyLimit.toLocaleString()} of ${dailyLimit.toLocaleString()} rows today. ${remaining.toLocaleString()} rows remaining. Please contact your admin to increase your limit.`,
      );
      return;
    }

    const csvHeaders = columns.join(",");
    const rows = results.map((row) =>
      columns
        .map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return "";
          if (shouldMask && isPiiColumn(col)) return "●●●●●●";
          const str = String(val);
          return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str.replace(/"/g, '""')}"` : str;
        })
        .join(","),
    );

    const csv = [csvHeaders, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report?.name || "report"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) return null;

  const manualFilters = parsedFilters.reportFilters;
  const configuredFilters = parsedFilters.configuredFilters;
  const displayResults = processedResults;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-muted/60 px-4 py-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex flex-col gap-0.5">
            <h2 className="text-3xl font-black tracking-tight truncate">{toTitleCase(report.name)}</h2>
            <div className="flex items-center gap-2">
              {report.description && (
                <span className="text-[10px] text-muted-foreground truncate">{report.description}</span>
              )}
              {lastRunAt && (
                <span className="text-[10px] text-muted-foreground">
                  {report.description ? "·" : ""} Last updated: {format(lastRunAt, "MMM d, yyyy · h:mm a")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={handleRun} disabled={running}>
            {running ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
            Run Report
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditQueryData({
                id: report.id,
                name: report.name,
                sql_query: report.sql_query,
                description: report.description,
                filter_config: report.filter_config,
                database_connection_id: report.database_connection_id,
                generation_mode: "manual",
              })}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit Query
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {results && results.length > 0 && (profile?.download_allowed ?? true) && (
            <Button size="sm" variant="outline" onClick={downloadCsv} disabled={downloadRemaining <= 0}>
              <Download className="mr-1 h-3.5 w-3.5" /> Download CSV
            </Button>
          )}
        </div>
      </div>

      {/* Configured filter bar (schema-aware) */}
      {configuredFilters.length > 0 && (
        <DashboardFilterBar filters={configuredFilters} dashboardId={reportId} onChange={setActiveFilterValues} />
      )}

      <div className="flex flex-1 min-h-0 overflow-hidden flex-col">
        {/* Placeholder Parameters */}
        {placeholderInfos.length > 0 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 mx-6 mt-4 shrink-0">
            <h3 className="text-sm font-semibold mb-1">Query Parameters</h3>
            <p className="text-xs text-muted-foreground mb-3">
              This report's query has placeholders. Fill in the values below before running.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {placeholderInfos.map((ph) => (
                <div key={ph.name} className="grid gap-1">
                  <Label className="text-xs">
                    {ph.name}
                    {ph.columnName && (
                      <span className="ml-1 text-muted-foreground font-normal">({ph.columnName})</span>
                    )}
                    <span className="ml-1 text-muted-foreground font-normal text-[10px]">[{ph.displayType}]</span>
                  </Label>
                  <PlaceholderInput
                    placeholder={ph}
                    value={placeholderValues[ph.name] ?? ""}
                    onChange={(v) =>
                      setPlaceholderValues((prev) => ({ ...prev, [ph.name]: v }))
                    }
                    sql={baseSql}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual Filters */}
        {manualFilters.length > 0 && (
          <div className="rounded-lg border border-border bg-card p-4 mx-6 mt-4 shrink-0">
            <h3 className="text-sm font-medium mb-3 flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Report Filters
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {manualFilters.map((f, idx) => (
                <div key={idx} className="grid gap-1">
                  <Label className="text-xs">
                    {f.label || f.column}
                    <span className="ml-1 text-muted-foreground font-normal">({f.operator})</span>
                  </Label>
                  {f.operator === "IS NULL" || f.operator === "IS NOT NULL" ? (
                    <span className="text-xs text-muted-foreground italic">No value needed</span>
                  ) : (
                    <Input
                      value={filterValues[f.column] ?? ""}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.column]: e.target.value }))}
                      placeholder={f.value || `Enter ${f.column}`}
                      className="h-8 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Download limit warning */}
        {(profile?.download_allowed ?? true) && downloadRemaining <= 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive mx-6 mt-4">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              You have reached your daily download limit of {dailyLimit.toLocaleString()} rows. Please contact your
              admin to increase your limit.
            </span>
          </div>
        )}
        {(profile?.download_allowed ?? true) &&
          results &&
          results.length > 0 &&
          results.length > downloadRemaining &&
          downloadRemaining > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-400 mx-6 mt-4">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                This report has {results.length.toLocaleString()} rows but you only have{" "}
                {downloadRemaining.toLocaleString()} rows remaining in your daily limit. Download will be blocked if it
                exceeds your limit.
              </span>
            </div>
          )}

        {/* Initial state — no results yet */}
        {displayResults === null && !running && (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-muted-foreground">
              Press &quot;Run Report&quot; to view data
            </p>
          </div>
        )}

        {/* Results table — full width */}
        {displayResults !== null && (
          <div className="flex-1 flex flex-col min-h-0 mt-4">
            {displayResults.length === 0 && results && results.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
                No results found
              </div>
            ) : (
              <>
                {/* Table toolbar */}
                <div className="px-6 py-2 bg-muted/50 border-y border-border flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {hasActiveColumnFilters
                      ? `${displayResults.length} of ${results!.length} row${results!.length !== 1 ? "s" : ""} (filtered)`
                      : `${results!.length} row${results!.length !== 1 ? "s" : ""}`}
                  </span>
                  <div className="flex items-center gap-2">
                    {hasActiveColumnFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={() => setColumnFilters({})}
                      >
                        <X className="h-3 w-3 mr-1" /> Clear filters
                      </Button>
                    )}
                    <Button
                      variant={showColumnFilters ? "secondary" : "ghost"}
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={() => setShowColumnFilters((v) => !v)}
                    >
                      {showColumnFilters ? "Hide filters" : "Show filters"}
                    </Button>
                  </div>
                </div>
                <div className="overflow-auto flex-1">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b bg-muted/60">
                        {columns.map((col) => {
                          const isActive = sortColumn === col;
                          return (
                            <th
                              key={col}
                              className="px-3 py-2 text-left font-medium whitespace-nowrap cursor-pointer select-none hover:bg-muted/80 transition-colors"
                              onClick={() => handleSort(col)}
                            >
                              <span className="inline-flex items-center gap-1">
                                {col}
                                {isActive && sortDirection === "asc" && <ArrowUp className="h-3 w-3 text-foreground" />}
                                {isActive && sortDirection === "desc" && (
                                  <ArrowDown className="h-3 w-3 text-foreground" />
                                )}
                                {!isActive && <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                      {showColumnFilters && (
                        <tr className="border-b bg-muted/30">
                          {columns.map((col) => (
                            <th key={col} className="px-2 py-1.5">
                              <Input
                                value={columnFilters[col] ?? ""}
                                onChange={(e) => setColumnFilters((prev) => ({ ...prev, [col]: e.target.value }))}
                                placeholder={`Filter ${col}...`}
                                className="h-6 text-xs font-normal"
                              />
                            </th>
                          ))}
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {displayResults.slice(0, 100).map((row, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                          {columns.map((col) => {
                            const val = row[col];
                            const isMaskedPii = shouldMask && isPiiColumn(col);
                            const unmaskKey = `report-row-${i}-col-${col}`;
                            return (
                              <td key={col} className="px-3 py-1.5 whitespace-nowrap max-w-[300px] truncate">
                                {val !== null && val !== undefined ? (
                                  isMaskedPii ? (
                                    <MaskedValue
                                      originalValue={String(val)}
                                      unmaskKey={unmaskKey}
                                      fieldName={col}
                                      isUnmasked={isUnmasked(unmaskKey)}
                                      onRequestUnmask={requestUnmask}
                                    />
                                  ) : (
                                    String(val)
                                  )
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {displayResults.length > 100 && (
                    <div className="px-4 py-2 text-xs text-muted-foreground text-center border-t">
                      Showing first 100 of {displayResults.length} rows.
                      {(profile?.download_allowed ?? true) ? " Download CSV for full data." : ""}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Query Editor Dialog */}
      <Dialog open={!!editQueryData} onOpenChange={(open) => { if (!open) setEditQueryData(null); }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          {editQueryData && (
            <QueryEditor
              editingQuery={editQueryData}
              onClose={() => setEditQueryData(null)}
              onSaved={(query) => {
                setReport((prev) => prev ? { ...prev, name: query.name, sql_query: query.sql_query } : prev);
                setEditQueryData(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportDownloadView;
